import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

let nextId = 0;

@Component({
  selector: 'aav-checkbox',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CheckboxComponent), multi: true },
  ],
  host: { class: 'inline-flex' },
  template: `
    <label
      [for]="inputId"
      class="group inline-flex items-center gap-2"
      [class.cursor-pointer]="!disabled"
      [class.cursor-not-allowed]="disabled"
      [class.opacity-40]="disabled"
    >
      <input
        [id]="inputId"
        type="checkbox"
        class="sr-only"
        [checked]="checked"
        [disabled]="disabled"
        [attr.name]="name || null"
        (change)="onToggle($event)"
        (focus)="focused = true"
        (blur)="onBlur()"
      />

      <span [class]="boxClasses">
        <aav-icon
          *ngIf="checked"
          name="check"
          [size]="14"
          class="text-aav-on-primary-container"
        ></aav-icon>
      </span>

      <span *ngIf="label" class="text-aav-body-medium text-aav-on-surface">{{ label }}</span>
    </label>
  `,
})
export class CheckboxComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() disabled = false;
  @Input() name = '';

  readonly inputId = `aav-checkbox-${nextId++}`;

  checked = false;
  focused = false;

  private onChange: (value: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get boxClasses(): string {
    const base =
      'flex h-4 w-4 shrink-0 items-center justify-center rounded-aav-xs border-2 transition-colors';

    const state = this.checked
      ? 'border-aav-primary-container bg-aav-primary-container'
      : 'border-aav-outline-variant bg-transparent group-hover:border-aav-on-surface-variant/40';

    const focusRing = this.focused ? 'ring-2 ring-aav-primary ring-offset-2' : '';

    return [base, state, focusRing].filter(Boolean).join(' ');
  }

  onToggle(event: Event): void {
    this.checked = (event.target as HTMLInputElement).checked;
    this.onChange(this.checked);
  }

  onBlur(): void {
    this.focused = false;
    this.onTouched();
  }

  writeValue(value: boolean | null): void {
    this.checked = value === true;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
